import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { uuidSchema } from "./platform-common.js";
export const ANALYSIS_OUTCOMES = ["succeeded", "degraded", "failed"];
export const DELIVERY_OUTCOMES = [
    "complete",
    "partial",
    "private-fallback",
    "not-delivered",
    "unconfirmed",
];
export const DEADLINE_REASONS = [
    "none",
    "queue",
    "context",
    "usage-admission",
    "source-setup",
    "evidence",
    "synthesis",
    "delivery",
    "shutdown",
];
export const CACHE_OUTCOMES = [
    "not-used",
    "hit",
    "miss",
    "coalesced",
    "refresh",
    "stale",
    "bypass",
    "skip-oversize",
    "load-error",
    "evicted",
    "mixed",
];
export const OPERATIONS_HEALTH_STATES = [
    "ready",
    "degraded",
    "idle",
    "unavailable",
    "stale",
];
export const REQUEST_FAMILIES = [
    "local-help",
    "local-evidence-link",
    "ability-leaderboard",
    "report-performance",
    "log-investigation",
    "generic-rotation",
    "encounter-guide",
    "guild-operations",
    "news",
    "general-current-wow",
];
export const EVIDENCE_ROLES = [
    "required",
    "supporting",
    "optional-peer",
    "requested-comparison",
];
export const EVIDENCE_SOURCES = [
    "local",
    "wcl",
    "simc",
    "wowlab",
    "simplify",
    "guild-source",
    "wowaudit",
    "news",
    "model",
    "database",
    "discord",
    "scheduler",
];
export const TOOL_OUTCOME_STATUSES = [
    "succeeded",
    "partial",
    "failed",
];
export const FAILURE_CODES = [
    "authorization_denied",
    "usage_denied",
    "usage_unavailable",
    "duplicate_request",
    "request_deadline",
    "queue_timeout",
    "provider_timeout",
    "provider_authentication",
    "provider_overload",
    "provider_protocol",
    "source_unavailable",
    "source_timeout",
    "source_rate_limited",
    "source_authentication",
    "source_configuration",
    "schema_incompatible",
    "source_transport",
    "source_protocol",
    "invalid_tool_input",
    "invalid_tool_result",
    "tool_incomplete",
    "journal_overflow",
    "journal_conflict",
    "guild_source_unavailable",
    "report_not_found",
    "fight_not_found",
    "ambiguous_scope",
    "delivery_failed",
    "delivery_unconfirmed",
    "unexpected_internal",
];
export const LIVE_SOURCE_NAMES = ["wcl", "wowlab", "simplify"];
export const SOURCE_LIFECYCLE_STATES = [
    "starting",
    "ready",
    "degraded",
    "reconnecting",
    "blocked",
    "stopped",
];
export const SOURCE_CIRCUIT_STATES = ["closed", "open", "half-open"];
export const RUNTIME_SOURCE_KINDS = ["guild-source", "wowaudit"];
export const OPERATIONS_WINDOWS = ["24h", "7d"];
export const OPERATIONS_LANES = [
    "bot",
    "logs",
    "guides",
    "simc",
    "guild-sources",
    "wowaudit",
];
export const OPERATIONS_PHASES = [
    "queue",
    "context",
    "evidence",
    "synthesis",
    "delivery",
];
export const SUPPORT_CODE_PATTERN = /^RL-[0-9A-HJKMNP-TV-Z]{16}$/u;
export const MAX_ASSISTANT_SOURCE_EVENTS = 12;
export const MAX_ASSISTANT_OPERATIONS_BATCH = 25;
export const analysisOutcomeSchema = z.enum(ANALYSIS_OUTCOMES);
export const deliveryOutcomeSchema = z.enum(DELIVERY_OUTCOMES);
export const deadlineReasonSchema = z.enum(DEADLINE_REASONS);
export const cacheOutcomeSchema = z.enum(CACHE_OUTCOMES);
export const operationsHealthStateSchema = z.enum(OPERATIONS_HEALTH_STATES);
export const requestFamilySchema = z.enum(REQUEST_FAMILIES);
export const evidenceRoleSchema = z.enum(EVIDENCE_ROLES);
export const evidenceSourceSchema = z.enum(EVIDENCE_SOURCES);
export const toolOutcomeStatusSchema = z.enum(TOOL_OUTCOME_STATUSES);
export const failureCodeSchema = z.enum(FAILURE_CODES);
export const liveSourceNameSchema = z.enum(LIVE_SOURCE_NAMES);
export const sourceLifecycleStateSchema = z.enum(SOURCE_LIFECYCLE_STATES);
export const sourceCircuitStateSchema = z.enum(SOURCE_CIRCUIT_STATES);
export const runtimeSourceKindSchema = z.enum(RUNTIME_SOURCE_KINDS);
export const operationsWindowSchema = z.enum(OPERATIONS_WINDOWS);
export const operationsLaneSchema = z.enum(OPERATIONS_LANES);
export const operationsPhaseSchema = z.enum(OPERATIONS_PHASES);
export const supportCodeSchema = z.string().regex(SUPPORT_CODE_PATTERN);
const boundedMetricSchema = z.int().nonnegative().max(2_147_483_647);
const boundedDurationSchema = z.int().nonnegative().max(600_000);
const boundedRegistryRevisionSchema = z.string().trim().min(1).max(128);
const nullableFailureCodeSchema = failureCodeSchema.nullable();
export const assistantCacheCountsSchema = z.strictObject({
    bypass: boundedMetricSchema,
    coalesced: boundedMetricSchema,
    evicted: boundedMetricSchema,
    hit: boundedMetricSchema,
    loadError: boundedMetricSchema,
    miss: boundedMetricSchema,
    notUsed: boundedMetricSchema,
    refresh: boundedMetricSchema,
    skipOversize: boundedMetricSchema,
    stale: boundedMetricSchema,
});
export const assistantProviderTokensSchema = z
    .strictObject({
    cacheRead: boundedMetricSchema,
    cacheWrite: boundedMetricSchema,
    input: boundedMetricSchema,
    output: boundedMetricSchema,
    reasoning: boundedMetricSchema,
    total: boundedMetricSchema,
})
    .superRefine((tokens, context) => {
    if (tokens.total < tokens.input + tokens.output) {
        context.addIssue({
            code: "custom",
            message: "Total tokens must cover input and output tokens",
            path: ["total"],
        });
    }
});
const assistantPhaseDurationFields = {
    context: boundedDurationSchema,
    delivery: boundedDurationSchema,
    evidence: boundedDurationSchema,
    queue: boundedDurationSchema,
    synthesis: boundedDurationSchema,
};
export const assistantPhaseDurationsSchema = z
    .strictObject({
    ...assistantPhaseDurationFields,
    total: boundedDurationSchema,
})
    .superRefine((durations, context) => {
    if (durations.total < Math.max(...Object.values(durations))) {
        context.addIssue({
            code: "custom",
            message: "Total duration must cover every measured phase",
            path: ["total"],
        });
    }
});
export const assistantExecutionCountsSchema = z.strictObject({
    modelRounds: z.int().nonnegative().max(16),
    modelSteps: z.int().nonnegative().max(64),
    toolExecutions: z.int().nonnegative().max(128),
    upstreamAttempts: z.int().nonnegative().max(256),
});
export const assistantEvidenceBytesSchema = z.strictObject({
    evidence: z
        .int()
        .nonnegative()
        .max(16 * 1_024 * 1_024),
    prompt: z
        .int()
        .nonnegative()
        .max(16 * 1_024 * 1_024),
    schemas: z
        .int()
        .nonnegative()
        .max(16 * 1_024 * 1_024),
});
export const assistantAnalysisSourceEventSchema = z
    .strictObject({
    ageSeconds: boundedMetricSchema.nullable(),
    attempts: z.int().nonnegative().max(256),
    cache: cacheOutcomeSchema,
    durationMs: boundedDurationSchema,
    finalFailureCode: nullableFailureCodeSchema,
    observedAt: utcTimestampSchema,
    role: evidenceRoleSchema,
    source: evidenceSourceSchema,
    stale: z.boolean(),
    status: toolOutcomeStatusSchema,
    transientFailures: z.int().nonnegative().max(256),
})
    .superRefine((event, context) => {
    if (event.transientFailures > event.attempts) {
        context.addIssue({
            code: "custom",
            message: "Transient failures cannot exceed attempts",
            path: ["transientFailures"],
        });
    }
    if (event.status !== "failed" && event.finalFailureCode !== null) {
        context.addIssue({
            code: "custom",
            message: "Usable source outcomes cannot retain a final failure code",
            path: ["finalFailureCode"],
        });
    }
    if (event.stale && event.ageSeconds === null) {
        context.addIssue({
            code: "custom",
            message: "Stale source evidence requires an age",
            path: ["ageSeconds"],
        });
    }
});
export const assistantAnalysisEventSchema = z
    .strictObject({
    analysisStatus: analysisOutcomeSchema,
    bytes: assistantEvidenceBytesSchema,
    cacheCounts: assistantCacheCountsSchema,
    completedAt: utcTimestampSchema,
    correlationId: uuidSchema,
    counts: assistantExecutionCountsSchema,
    deadlineReason: deadlineReasonSchema,
    deliveryStatus: deliveryOutcomeSchema,
    family: requestFamilySchema,
    guildId: discordSnowflakeSchema.nullable(),
    phaseDurationMs: assistantPhaseDurationsSchema,
    safeFailureCode: nullableFailureCodeSchema,
    scope: z.enum(["guild", "dm"]),
    sources: z
        .array(assistantAnalysisSourceEventSchema)
        .max(MAX_ASSISTANT_SOURCE_EVENTS),
    startedAt: utcTimestampSchema,
    supportCode: supportCodeSchema,
    tokens: assistantProviderTokensSchema,
})
    .superRefine((event, context) => {
    if ((event.scope === "guild") !== (event.guildId !== null)) {
        context.addIssue({
            code: "custom",
            message: "Guild scope requires a guild ID and DM scope forbids one",
            path: ["guildId"],
        });
    }
    if (Date.parse(event.completedAt) < Date.parse(event.startedAt)) {
        context.addIssue({
            code: "custom",
            message: "Completion cannot precede request start",
            path: ["completedAt"],
        });
    }
    const sources = event.sources.map(({ source }) => source);
    if (new Set(sources).size !== sources.length) {
        context.addIssue({
            code: "custom",
            message: "Source events must be aggregated by source",
            path: ["sources"],
        });
    }
});
export const assistantAnalysisBatchSchema = z
    .array(assistantAnalysisEventSchema)
    .min(1)
    .max(MAX_ASSISTANT_OPERATIONS_BATCH)
    .superRefine((events, context) => {
    const correlationIds = events.map(({ correlationId }) => correlationId);
    if (new Set(correlationIds).size !== correlationIds.length) {
        context.addIssue({
            code: "custom",
            message: "A batch cannot repeat a correlation ID",
        });
    }
});
export const botSourceHealthUpdateSchema = z.strictObject({
    circuitState: sourceCircuitStateSchema,
    instanceId: uuidSchema,
    lastFailureAt: utcTimestampSchema.nullable(),
    lastSuccessAt: utcTimestampSchema.nullable(),
    lifecycleState: sourceLifecycleStateSchema,
    registryRevision: boundedRegistryRevisionSchema,
    retryAt: utcTimestampSchema.nullable(),
    safeFailureCode: nullableFailureCodeSchema,
    source: liveSourceNameSchema,
    updatedAt: utcTimestampSchema,
});
const runtimeSourceIdSchema = z.string().trim().min(1).max(36);
export const guildRuntimeSourceHealthUpdateSchema = z
    .strictObject({
    ageSeconds: boundedMetricSchema.nullable(),
    guildId: discordSnowflakeSchema,
    lastSuccessAt: utcTimestampSchema.nullable(),
    safeFailureCode: nullableFailureCodeSchema,
    serving: operationsHealthStateSchema,
    sourceId: runtimeSourceIdSchema,
    sourceKind: runtimeSourceKindSchema,
    sourceRevision: z.int().positive().max(2_147_483_647),
    stale: z.boolean(),
    updatedAt: utcTimestampSchema,
})
    .superRefine((health, context) => {
    if (health.sourceKind === "wowaudit" && health.sourceId !== "wowaudit") {
        context.addIssue({
            code: "custom",
            message: "WoWAudit runtime health uses the singleton source ID",
            path: ["sourceId"],
        });
    }
    if (health.sourceKind === "guild-source" &&
        !uuidSchema.safeParse(health.sourceId).success) {
        context.addIssue({
            code: "custom",
            message: "Guild-source runtime health requires an opaque UUID",
            path: ["sourceId"],
        });
    }
    if (health.stale && health.ageSeconds === null) {
        context.addIssue({
            code: "custom",
            message: "Stale runtime health requires an age",
            path: ["ageSeconds"],
        });
    }
});
export const botOperationsHealthUpdateSchema = z.strictObject({
    hardDeadlineViolations: boundedMetricSchema,
    instanceId: uuidSchema,
    lastSeenAt: utcTimestampSchema,
    settlementOverflows: boundedMetricSchema,
    telemetryDrops: boundedMetricSchema,
});
export const configuredSourceHealthSchema = z.strictObject({
    ageSeconds: boundedMetricSchema.nullable(),
    enabled: z.boolean(),
    lastSuccessAt: utcTimestampSchema.nullable(),
    safeFailureCode: nullableFailureCodeSchema,
    serving: operationsHealthStateSchema,
    sourceId: runtimeSourceIdSchema,
    validation: z.enum(["pending", "claimed", "valid", "invalid"]),
});
export const assistantRequestSummarySchema = z.strictObject({
    analysisStatus: analysisOutcomeSchema,
    completedAt: utcTimestampSchema,
    deadlineReason: deadlineReasonSchema,
    deliveryStatus: deliveryOutcomeSchema,
    family: requestFamilySchema,
    safeFailureCode: nullableFailureCodeSchema,
    supportCode: supportCodeSchema,
    totalDurationMs: boundedDurationSchema,
});
export const assistantRequestListResponseSchema = z.strictObject({
    items: z.array(assistantRequestSummarySchema).max(50),
    nextCursor: z.string().min(1).max(128).nullable(),
    requestId: z.string().trim().min(1).max(128),
    window: operationsWindowSchema,
});
const assistantRequestDetailSourceSchema = z.strictObject({
    ageSeconds: boundedMetricSchema.nullable(),
    attempts: z.int().nonnegative().max(256),
    cache: cacheOutcomeSchema,
    durationMs: boundedDurationSchema,
    role: evidenceRoleSchema,
    safeFailureCode: nullableFailureCodeSchema,
    source: evidenceSourceSchema,
    stale: z.boolean(),
    status: toolOutcomeStatusSchema,
});
export const assistantRequestDetailResponseSchema = assistantRequestSummarySchema.extend({
    bytes: assistantEvidenceBytesSchema,
    counts: assistantExecutionCountsSchema,
    phaseDurationMs: z.strictObject(assistantPhaseDurationFields),
    requestId: z.string().trim().min(1).max(128),
    sources: z
        .array(assistantRequestDetailSourceSchema)
        .max(MAX_ASSISTANT_SOURCE_EVENTS),
});
const latencyMetricSchema = z.int().nonnegative().max(600_000).nullable();
const operationsLaneHealthSchema = z.strictObject({
    ageSeconds: boundedMetricSchema.nullable(),
    lane: operationsLaneSchema,
    lastSuccessAt: utcTimestampSchema.nullable(),
    safeFailureCode: nullableFailureCodeSchema,
    state: operationsHealthStateSchema,
});
const operationsPhaseHealthSchema = z.strictObject({
    p50Ms: latencyMetricSchema,
    p95Ms: latencyMetricSchema,
    phase: operationsPhaseSchema,
    sampleSize: boundedMetricSchema,
});
const operationsHealthBaseShape = {
    capturedAt: utcTimestampSchema,
    counts: z.strictObject({
        degraded: boundedMetricSchema,
        failed: boundedMetricSchema,
        succeeded: boundedMetricSchema,
    }),
    deadlineCounts: z.strictObject({
        context: boundedMetricSchema,
        delivery: boundedMetricSchema,
        evidence: boundedMetricSchema,
        queue: boundedMetricSchema,
        shutdown: boundedMetricSchema,
        sourceSetup: boundedMetricSchema,
        synthesis: boundedMetricSchema,
        usageAdmission: boundedMetricSchema,
    }),
    lanes: z.array(operationsLaneHealthSchema).max(OPERATIONS_LANES.length),
    latency: z.strictObject({
        hardDeadlineViolations: boundedMetricSchema,
        p50Ms: latencyMetricSchema,
        p95Ms: latencyMetricSchema,
        state: z.enum(["ready", "slow", "critical", "insufficient-sample"]),
    }),
    phases: z.array(operationsPhaseHealthSchema).max(OPERATIONS_PHASES.length),
    readiness: z.enum([
        "ready",
        "degraded",
        "unavailable",
        "insufficient-sample",
    ]),
    requestId: z.string().trim().min(1).max(128),
    sampleSize: boundedMetricSchema,
    timeoutCounts: z.strictObject({
        deliveryUnconfirmed: boundedMetricSchema,
        provider: boundedMetricSchema,
        queue: boundedMetricSchema,
        source: boundedMetricSchema,
    }),
    window: operationsWindowSchema,
};
export const adminDmAggregateSchema = z.strictObject({
    analysisStatus: analysisOutcomeSchema,
    family: requestFamilySchema,
    p50DurationMs: latencyMetricSchema,
    p95DurationMs: latencyMetricSchema,
    requestCount: boundedMetricSchema.min(10),
});
const fleetInstanceHealthSchema = z.strictObject({
    hardDeadlineViolations: boundedMetricSchema,
    instanceId: uuidSchema,
    lastSeenAt: utcTimestampSchema,
    settlementOverflows: boundedMetricSchema,
    state: z.enum(["ready", "degraded", "unavailable", "stale"]),
    telemetryDrops: boundedMetricSchema,
});
export const guildOperationsHealthResponseSchema = z
    .strictObject({
    ...operationsHealthBaseShape,
    configuredSources: z.array(configuredSourceHealthSchema).max(100),
    scope: z.strictObject({
        guildId: discordSnowflakeSchema,
        kind: z.literal("guild"),
    }),
})
    .superRefine((health, context) => {
    validateHealthCollections(health, context);
    validateUnique(health.configuredSources.map(({ sourceId }) => sourceId), context, ["configuredSources"], "Configured source IDs must be unique");
});
export const fleetOperationsHealthResponseSchema = z
    .strictObject({
    ...operationsHealthBaseShape,
    dmAggregates: z.array(adminDmAggregateSchema).max(60),
    instances: z.array(fleetInstanceHealthSchema).max(100),
    scope: z.strictObject({ kind: z.literal("fleet") }),
    visibleDmRequestCount: boundedMetricSchema,
})
    .superRefine((health, context) => {
    validateHealthCollections(health, context);
    const visibleDmRequestCount = health.dmAggregates.reduce((total, aggregate) => total + aggregate.requestCount, 0);
    if (health.visibleDmRequestCount !== visibleDmRequestCount) {
        context.addIssue({
            code: "custom",
            message: "Visible DM count must equal the returned aggregate count",
            path: ["visibleDmRequestCount"],
        });
    }
    validateUnique(health.instances.map(({ instanceId }) => instanceId), context, ["instances"], "Fleet instance IDs must be unique");
    validateUnique(health.dmAggregates.map(({ family, analysisStatus }) => `${family}:${analysisStatus}`), context, ["dmAggregates"], "DM aggregate groups must be unique");
});
export const operationsHealthResponseSchema = z.union([
    guildOperationsHealthResponseSchema,
    fleetOperationsHealthResponseSchema,
]);
function validateHealthCollections(health, context) {
    const lanes = health.lanes.map(({ lane }) => lane);
    if (lanes.length !== OPERATIONS_LANES.length ||
        new Set(lanes).size !== OPERATIONS_LANES.length ||
        OPERATIONS_LANES.some((lane) => !lanes.includes(lane))) {
        context.addIssue({
            code: "custom",
            message: "Operations health must include every lane exactly once",
            path: ["lanes"],
        });
    }
    const phases = health.phases.map(({ phase }) => phase);
    if (phases.length !== OPERATIONS_PHASES.length ||
        new Set(phases).size !== OPERATIONS_PHASES.length ||
        OPERATIONS_PHASES.some((phase) => !phases.includes(phase))) {
        context.addIssue({
            code: "custom",
            message: "Operations health must include every phase exactly once",
            path: ["phases"],
        });
    }
}
function validateUnique(values, context, path, message) {
    if (new Set(values).size !== values.length) {
        context.addIssue({ code: "custom", message, path });
    }
}
//# sourceMappingURL=assistant-operations.js.map